import { computed, reactive, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useAsyncState } from '@vueuse/core';
import { defineStore, acceptHMRUpdate } from 'pinia';
import { normalizeInteger, normalizeNumericValue, normalizeString, normalizeStringOrNull } from '@dashboardApp/helpers/normalize';
import { jsonapiPreservedStore, sortToPreservedJson } from '@dashboardApp/store';
import useInsights from '@dashboardApp/composables/useInsights';
import useProductIdeaSaveToggle from '@dashboardApp/composables/useProductIdeaSaveToggle';
import router from '@dashboardApp/router';

const PRODUCT_EXPANSION_ROUTE = 'research-product-expansion';
const DEFAULT_LIMIT = 3;
const MAX_LIMIT = 12;

function createDefaultFilters() {
    return {
        query: '',
    };
}

export const useProductExpansionStore = defineStore('ProductExpansionStore', () => {
    const insights = useInsights();
    const route = useRoute();
    const preservedStore = jsonapiPreservedStore();
    const ideas = ref([]);
    const filters = reactive(createDefaultFilters());
    const hasSearchQuery = computed(() => Boolean(filters.query));
    const presentedIdeas = computed(() => ideas.value.map((idea) => mapProductExpansionIdeaToViewModel(idea, insights)));

    const {
        execute: fetchIdeas,
        isReady: ideasAreReady,
        isLoading: ideasAreLoading,
    } = useAsyncState(fetchIdeasPromise, [], { immediate: false });

    function fetchIdeasPromise(options = {}) {
        const query = normalizeStringOrNull(options.query ?? filters.query);
        const limit = Math.min(normalizeInteger(options.limit ?? DEFAULT_LIMIT, DEFAULT_LIMIT), MAX_LIMIT);

        if (!query) {
            ideas.value = [];
            return Promise.resolve(ideas.value);
        }

        const params = {
            page: {
                limit,
            },
            filter: {
                query,
            },
        };

        return preservedStore.get(['product-expansion', { params }]).then((data) => {
            ideas.value = sortToPreservedJson(data);
            return ideas.value;
        });
    }

    function setQuery(value) {
        filters.query = normalizeString(value);
    }

    function search(value = filters.query) {
        filters.query = normalizeString(value);

        return router.push({
            name: PRODUCT_EXPANSION_ROUTE,
            query: buildRouteQuery(),
        });
    }

    function buildRouteQuery() {
        const query = {};

        if (filters.query) {
            query.query = filters.query;
        }

        return query;
    }

    function applyRouteQuery(routeQuery = {}) {
        const defaults = createDefaultFilters();
        filters.query = normalizeString(routeQuery.query ?? routeQuery.term ?? defaults.query);
    }

    function fetchFromRoute(routeQuery = route.query) {
        applyRouteQuery(routeQuery);
        return fetchIdeas(0, { query: filters.query, limit: DEFAULT_LIMIT });
    }

    return {
        filters,
        hasSearchQuery,
        ideas,
        presentedIdeas,
        ideasAreReady,
        ideasAreLoading,
        fetchIdeas,
        fetchFromRoute,
        setQuery,
        search,
    };
});

function mapProductExpansionIdeaToViewModel(idea, insights) {
    const title = normalizeStringOrNull(idea?.title) || 'Untitled product';
    const saveToggle = useProductIdeaSaveToggle({
        id: idea?._jv?.id ?? idea?.product_id ?? idea?.id ?? null,
        name: title,
        title,
        term: title,
        report_id: idea?.report_id ?? null,
    });
    const category = normalizeStringOrNull(idea?.category) || 'General';
    const trendValue = normalizeTrendValue(idea?.interest_trend?.change_pct);
    const opportunity = humanizeOpportunity(idea?.market_opportunity);
    const demand = humanizeLevel(idea?.demand_level);
    const competition = humanizeLevel(idea?.competition_level);
    const reportRoute = buildReportRoute(idea?.slug, idea?.interest_trend?.window);

    return {
        id: normalizeStringOrNull(idea?.id ?? idea?.slug ?? title) || title,
        title,
        description: buildDescription(idea?.summary, category, trendValue),
        insight: buildInsight(idea?.key_insights, opportunity, demand, competition, category),
        metrics: [
            { label: 'Demand', value: demand, badge: formatPercentage(idea?.demand_percentage) },
            { label: 'Competition', value: competition, badge: formatPercentage(idea?.competition_percentage) },
        ],
        actions: buildActions(reportRoute, title, insights, saveToggle),
    };
}

function buildDescription(summary, category, trendValue) {
    const normalizedSummary = normalizeStringOrNull(summary);
    if (normalizedSummary) {
        return normalizedSummary;
    }

    const trendText = trendValue === null
        ? 'market momentum'
        : `${formatTrend(trendValue).toLowerCase()} in interest`;

    return `Broadly related ${category.toLowerCase()} opportunity with ${trendText}.`;
}

function buildInsight(keyInsights, opportunity, demand, competition, category) {
    const primaryInsight = Array.isArray(keyInsights)
        ? normalizeStringOrNull(keyInsights.map(extractInsightText).find(Boolean))
        : null;

    if (primaryInsight) {
        return primaryInsight;
    }

    return `${category} shows ${demand.toLowerCase()} demand, ${competition.toLowerCase()} competition, and ${opportunity.toLowerCase()} profit potential for adjacent expansion.`;
}

function extractInsightText(value) {
    if (typeof value === 'string') {
        return value;
    }

    if (!value || typeof value !== 'object') {
        return null;
    }

    return value.text || value.summary || value.title || value.label || null;
}

function buildReportRoute(slug, reportingPeriod) {
    const normalizedSlug = normalizeStringOrNull(slug);
    if (!normalizedSlug) {
        return null;
    }

    return {
        name: 'research-trending-product-idea-report',
        params: { slug: normalizedSlug },
        query: {
            interest_trend_window: normalizeStringOrNull(reportingPeriod) || '12mo',
        },
    };
}

function buildActions(reportRoute, title, insights, saveToggle) {
    const actions = [];

    if (reportRoute) {
        actions.push({
            label: 'Full report',
            icon: '/img/v5/db_ic_doc-white.svg',
            to: reportRoute,
            variant: 'primary',
        });
    }

    actions.push({
        label: 'Source',
        icon: '/img/v5/db_ic_box.svg',
        onClick: () => insights.sourceProduct(title),
        disabled: insights.sourcingInProgress,
        variant: 'secondary',
    });

    actions.push({
        label: computed(() => saveToggle.isReported.value ? 'Remove Idea' : 'Save Idea'),
        icon: computed(() => saveToggle.isReported.value ? '/img/v5/db_ic_trash.svg' : '/img/v5/db_ic_recommend.svg'),
        onClick: saveToggle.saveOrRemove,
        disabled: saveToggle.reportMutationInProgress,
        variant: 'secondary',
    });

    return actions;
}

function normalizeTrendValue(value) {
    if (value === null || value === undefined || value === '') {
        return null;
    }

    const normalized = Number.parseFloat(value);
    return Number.isFinite(normalized) ? normalized : null;
}

function formatTrend(value) {
    if (value === null) {
        return 'N/A';
    }

    const prefix = value > 0 ? '+' : '';
    return `${prefix}${normalizeNumericValue(value)}%`;
}

function formatPercentage(value) {
    if (value === null || value === undefined || value === '') {
        return null;
    }

    return `${normalizeNumericValue(value)}%`;
}

function formatCurrency(value) {
    if (value === null || value === undefined || value === '') {
        return 'N/A';
    }

    const normalized = Number.parseFloat(value);
    if (!Number.isFinite(normalized)) {
        return 'N/A';
    }

    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: normalized < 100 ? 2 : 0,
    }).format(normalized);
}

function humanizeOpportunity(value) {
    const normalized = normalizeString(value).replace(/[_-]+/g, ' ').toLowerCase();

    if (!normalized) {
        return 'Unknown';
    }

    return normalized.replace(/(^|\s)\w/g, (match) => match.toUpperCase());
}

function humanizeLevel(value) {
    const normalized = normalizeString(value).toLowerCase();

    if (!normalized) {
        return 'Unknown';
    }

    return normalized.charAt(0).toUpperCase() + normalized.slice(1);
}

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useProductExpansionStore, import.meta.hot));
}
